
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { FormDialog } from "@/components/shared/FormDialog";
import { getCategoryColumns } from "./ProductColumns";
import { CategoryForm, CategoryFormValues } from "./CategoryForm";
import { useCategories } from "@/hooks/useCategories";
import { useFactories } from "@/hooks/useFactories";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";

export function CategoriesManager() {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const { factories, loading: factoriesLoading } = useFactories();
  
  const {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory
  } = useCategories();
  
  const categoryColumns = getCategoryColumns();

  const handleSubmit = async (values: CategoryFormValues) => {
    try {
      let success;
      
      if (editingCategory) {
        success = await updateCategory(editingCategory.id, values);
      } else {
        success = await createCategory(values);
      }

      if (success) {
        setOpenDialog(false);
        setEditingCategory(null);
      }
    } catch (error: any) {
      console.error("Error submitting product:", error);
    }
  };

  const handleEdit = (category: any) => {
    setEditingCategory(category);
    setOpenDialog(true);
  };

  const handleDelete = async (category: any) => {
    return deleteCategory(category);
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setOpenDialog(true);
  };

  const defaultValues = editingCategory ? {
    name: editingCategory.name,
    description: editingCategory.description || "",
    product_prefix: editingCategory.product_prefix || "",
    image_url: editingCategory.image_url || "",
    factories: Array.isArray(editingCategory.factories) ? editingCategory.factories : [],
    is_bundle: editingCategory.is_bundle !== undefined ? editingCategory.is_bundle : true,
    bundle_products: Array.isArray(editingCategory.bundle_products) ? editingCategory.bundle_products : [],
    sort_order: editingCategory.sort_order || 999,
  } : {
    name: "",
    description: "",
    product_prefix: "",
    image_url: "",
    factories: [],
    is_bundle: true,
    bundle_products: [],
    sort_order: 999,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Management</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Products</h3>
          <Button onClick={handleAddNew} className="flex items-center gap-2">
            <PlusCircle className="h-4 w-4" />
            Add Product
          </Button>
        </div>
        
        <DataTable
          data={categories}
          columns={categoryColumns}
          onAdd={handleAddNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
          title="Products"
          itemName="Product"
          isLoading={loading}
          searchColumns={["factories"]}
        />
        
        <FormDialog 
          title={editingCategory ? "Edit Product" : "Add New Product"} 
          open={openDialog} 
          onOpenChange={setOpenDialog}
        >
          <CategoryForm
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            onCancel={() => setOpenDialog(false)}
            isEditing={!!editingCategory}
            factories={factories}
            isLoadingFactories={factoriesLoading}
            allProducts={categories}
          />
        </FormDialog>
      </CardContent>
    </Card>
  );
}
